import React, { useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Plus, 
  Settings, 
  Share2, 
  FileText,
  Home,
  Bookmark,
  Folder,
  LucideIcon,
  Highlighter,
  CheckSquare,
  Edit3,
  Trash2,
  Eye,
  ExternalLink,
  ArrowRight,
  Link as LinkIcon,
  Pause,
  Moon,
  X as XIcon,
  Grid3x3,
  MoreHorizontal,
  Sparkles,
  Layers,
  SquareStack,
  RefreshCw,
  MessageCircle,
} from 'lucide-react';
import { clsx } from 'clsx';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffectiveViewport } from '@/hooks/useEffectiveViewport';
import { AddGroupModal } from '../modals/AddGroupModal';
import { CreateTabsGroupModal, type CreateTabsGroupResult } from '../modals/CreateTabsGroupModal';
import { EditTabsGroupModal } from '../modals/EditTabsGroupModal';
import { LearnRulePrompt } from '@/components/sessions/LearnRulePrompt';
import { MoveSessionModal } from '../modals/MoveSessionModal';
import { BookmarkModal } from '../modals/BookmarkModal';
import { DeleteSessionModal } from '../modals/DeleteSessionModal';
import {
  SyncProjectsConflictModal,
  type SyncConflictProject,
} from '../modals/SyncProjectsConflictModal';
import { ShareProjectModal } from '../modals/ShareProjectModal';
import { NotificationBell } from '../notifications/NotificationBell';
import api from '@/api';
import { useExtensionUtil, hasExtension as detectExtension, useHasExtension } from '@/hooks/useExtensionUtil';
import { loadSessionsViaExtensionOrApi } from '@/utils/extensionSessions';
import { loadSessionTabsViaExtensionOrApi } from '@/utils/extensionSessionTabs';
import { useSessionsStore } from '@/stores/sessions-store';
import Logger from '@/utils/logger';
import { notifications } from '@/utils/notifications';
import { isElectron } from '@/utils/platform';

import { Session } from '@/types/sessions.types';
import {
  DEFAULT_AUTO_SUSPEND_CONFIG,
  type AutoSuspendConfig,
} from '@/types/auto-suspend';

const VIEWPORT_PAD = 8;
const SESSION_MENU_GAP = 4;
const SESSION_MENU_WIDTH = 220;
const SESSION_MENU_EST_HEIGHT = 420;

/** Open beside the ⋮ trigger (to the right when possible) so the project row stays visible. */
function getSessionMenuPosition(
  anchor: DOMRect,
  menuWidth: number,
  menuHeight: number
): { x: number; y: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let x = anchor.right + SESSION_MENU_GAP;
  if (x + menuWidth > vw - VIEWPORT_PAD) {
    x = anchor.left - menuWidth - SESSION_MENU_GAP;
  }
  x = Math.max(VIEWPORT_PAD, Math.min(x, vw - menuWidth - VIEWPORT_PAD));

  // Align menu top with the ⋮ trigger (not below the row).
  let y = anchor.top;
  if (y + menuHeight > vh - VIEWPORT_PAD) {
    const below = anchor.bottom + SESSION_MENU_GAP;
    if (below + menuHeight <= vh - VIEWPORT_PAD) {
      y = below;
    } else {
      y = Math.max(VIEWPORT_PAD, anchor.top - menuHeight - SESSION_MENU_GAP);
    }
  }
  return { x, y };
}

function getSessionMenuAnchor(row: HTMLElement): DOMRect {
  const trigger = row.querySelector<HTMLElement>('[data-session-more-trigger]');
  return (trigger ?? row).getBoundingClientRect();
}

/** Shape of session-update events broadcast by the extension service worker. */
interface SessionUpdateEvent {
  type?: string;
  csid?: string;
  sid?: string;
  name?: string;
  icon?: string;
  closeTabsOnSessionChange?: boolean;
  autoSuspend?: AutoSuspendConfig;
  sleepingTabCount?: number;
  activeSessions?: string[];
  status?: string;
  reason?: string;
  host?: string;
  sessionName?: string;
  tabId?: string | number;
  oldTabId?: string | number;
  inx?: number;
  tab?: { tabId?: string; title?: string; url?: string; favIconUrl?: string };
  session?: Session;
  removed?: number;
  data?: {
    csession?: { sid?: string; tabId?: (string | number)[] };
    nsession?: { sid?: string };
  };
}

// --- Types ---

export interface MenuItem {
  id: string;
  label: string;
  icon: LucideIcon;
  count?: number;
  /** Auto-suspended tab count for session row badge */
  sleepingCount?: number;
  color?: string;
  to?: string;
  /** Emoji from API (e.g. session icon hex); when set, shown instead of icon */
  iconEmoji?: string;
}

export interface MenuSection {
  /** Internal id for session/workspace logic */
  title: 'UNSAVED' | 'PROJECTS';
  /** i18n key under `nav` namespace (e.g. sections.unsaved) */
  titleKey: string;
  type?: 'tabs' | 'troves';
  items: MenuItem[];
  showAdd?: boolean;
}

interface SubSidebarProps {
  activeItem?: string;
  onItemSelect?: (id: string) => void;
  onTabDropped?: () => void;
  /** When set, sidebar is a fixed drawer on small screens (controlled). */
  mobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
}

// --- Config ---

const PRIMARY_NAV_BASE = [
  { id: 'home', icon: Home, to: '/' },
  { id: 'sessions', icon: Layers, to: '/tabs' },
  { id: 'troves', icon: Bookmark, to: '/troves' },
  { id: 'docs', icon: FileText, to: '/notes' },
  { id: 'highlighter', icon: Highlighter, to: '/highlighter' },
  { id: 'tasks', icon: CheckSquare, to: '/tasks' },
  { id: 'chat', icon: MessageCircle, to: '/chat' },
  { id: 'assistant', icon: Sparkles, to: '/assistant' },
  { id: 'shared-with-me', icon: Share2, to: '/shared-with-me' },
] as const;

/** Bottom bar on compact viewports — rest live in the “More” drawer. */
const MOBILE_BOTTOM_NAV_IDS = new Set(['home', 'sessions', 'troves', 'tasks']);

function usePrimaryNav() {
  return PRIMARY_NAV_BASE;
}

function useMobileBottomNav() {
  return PRIMARY_NAV_BASE.filter((n) => MOBILE_BOTTOM_NAV_IDS.has(n.id));
}

function useMobileOverflowNav() {
  return PRIMARY_NAV_BASE.filter((n) => !MOBILE_BOTTOM_NAV_IDS.has(n.id));
}

/** Settings & overflow destinations — mobile “More” drawer (desktop uses GlobalIconBar). */
export function SecondaryNavContent({ onNavigate }: { onNavigate?: () => void }) {
  const { t: tn } = useTranslation('nav');
  const overflowNav = useMobileOverflowNav();

  const itemClass = (isActive: boolean) =>
    clsx(
      'flex min-h-[3.25rem] items-center gap-3.5 rounded-xl px-4 py-3 text-[15px] font-medium leading-snug transition-colors',
      isActive
        ? 'bg-primary/10 text-primary'
        : 'text-foreground/90 active:bg-muted/60',
    );

  const iconWrapClass = (isActive: boolean) =>
    clsx(
      'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
      isActive ? 'bg-primary/15 text-primary' : 'bg-muted/50 text-muted-foreground',
    );

  return (
    <div className="flex flex-col gap-1 p-3 pb-6">
      {overflowNav.map((nav) => (
        <NavLink
          key={nav.id}
          to={nav.to}
          onClick={() => onNavigate?.()}
          className={({ isActive }) => itemClass(isActive)}
        >
          {({ isActive }) => (
            <>
              <span className={iconWrapClass(isActive)}>
                <nav.icon size={22} strokeWidth={isActive ? 2.25 : 2} aria-hidden />
              </span>
              <span className="min-w-0 flex-1">{tn(`primary.${nav.id}.full` as 'primary.home.full')}</span>
            </>
          )}
        </NavLink>
      ))}
      <div className="my-2 border-t border-border" />
      <NavLink
        to="/settings"
        onClick={() => onNavigate?.()}
        className={({ isActive }) => itemClass(isActive)}
      >
        {({ isActive }) => (
          <>
            <span className={iconWrapClass(isActive)}>
              <Settings size={22} strokeWidth={isActive ? 2.25 : 2} aria-hidden />
            </span>
            <span className="min-w-0 flex-1">{tn('secondary.settingsProfile')}</span>
          </>
        )}
      </NavLink>
    </div>
  );
}

/** Fixed bottom bar (md+: hidden) — same destinations as GlobalIconBar + More. */
export function MobileBottomNav({ onMore }: { onMore: () => void }) {
  const { t: tn } = useTranslation('nav');
  const { isCompact } = useEffectiveViewport();
  const mobileNav = useMobileBottomNav();
  if (!isCompact) return null;
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 pb-[env(safe-area-inset-bottom,0px)] backdrop-blur-md supports-[backdrop-filter]:bg-background/80"
      aria-label={tn('aria.mainNav')}
    >
      <div className="flex h-14 min-h-[3.5rem] items-stretch justify-around px-1">
        {mobileNav.map((nav) => (
          <NavLink
            key={nav.id}
            to={nav.to}
            end={nav.to === '/'}
            title={tn(`primary.${nav.id}.full` as 'primary.home.full')}
            aria-label={tn(`primary.${nav.id}.full` as 'primary.home.full')}
            className={({ isActive }) =>
              clsx(
                'flex min-w-0 flex-1 max-w-[4.5rem] flex-col items-center justify-center gap-0.5 py-1 transition-colors',
                isActive ? 'text-primary' : 'text-muted-foreground active:bg-muted/40',
              )
            }
          >
            {({ isActive }) => (
              <span
                className={clsx(
                  'flex h-9 w-9 items-center justify-center rounded-xl transition-colors',
                  isActive && 'bg-primary/10',
                )}
              >
                <nav.icon size={22} strokeWidth={isActive ? 2.5 : 2} aria-hidden />
              </span>
            )}
          </NavLink>
        ))}
        <button
          type="button"
          onClick={onMore}
          title={tn('more')}
          aria-label={tn('aria.moreOptions')}
          className="flex min-w-0 flex-1 max-w-[4.5rem] flex-col items-center justify-center gap-0.5 py-1 text-muted-foreground transition-colors active:bg-muted/40"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-xl">
            <MoreHorizontal size={22} strokeWidth={2} aria-hidden />
          </span>
        </button>
      </div>
    </nav>
  );
}

const MENU_CONFIG: MenuSection[] = [
  {
    title: 'UNSAVED',
    titleKey: 'sections.unsaved',
    type: 'tabs',
    showAdd: true,
    items: [],
  },
  {
    title: 'PROJECTS',
    titleKey: 'sections.projects',
    type: 'tabs',
    showAdd: true,
    items: [],
  },
];

function sessionToMenuItem(session: Session): MenuItem {
  let iconEmoji: string | undefined;
  if (session.icon) {
    // Check if it's a hex string (e.g., "1F4C1")
    if (/^[0-9A-Fa-f]+$/.test(session.icon)) {
      try {
        iconEmoji = String.fromCodePoint(parseInt(session.icon, 16));
      } catch {
        // If conversion fails, use the icon as-is
        iconEmoji = session.icon;
      }
    } else {
      // Icon is already an emoji or string, use it directly
      iconEmoji = session.icon;
    }
  }
  return {
    id: session.sid,
    label: session.name,
    icon: Folder,
    color: 'text-yellow-500',
    iconEmoji,
    sleepingCount:
      typeof session.sleepingTabCount === 'number' && session.sleepingTabCount > 0
        ? session.sleepingTabCount
        : undefined,
  };
}

// --- Components ---

/**
 * Pane 1: Global narrow icon bar
 */
export const GlobalIconBar = () => {
  const { isWide } = useEffectiveViewport();
  const { t: tn } = useTranslation('nav');
  const primaryNav = usePrimaryNav();

  if (!isWide) return null;

  return (
    <aside className="z-50 flex h-dvh max-h-dvh w-16 shrink-0 flex-col self-start overflow-hidden border-r border-border bg-sidebar-1">
      <div className="icon-bar-scroll flex min-h-0 flex-1 flex-col items-center overflow-x-hidden overflow-y-auto overscroll-y-contain py-4">
      {/* Brand Logo */}
      <div className="w-10 h-10 flex items-center justify-center mb-8 shrink-0">
        <img
          src="/newlogo.svg"
          alt="Trove logo"
          className="w-7 h-7 object-contain"
        />
      </div>

      {/* Primary Icons */}
      <nav className="flex w-full flex-col items-center gap-4 shrink-0" aria-label={tn('aria.mainNav')}>
        {primaryNav.map((nav) => (
          <NavLink
            key={nav.id}
            to={nav.to}
            end={nav.to === '/'}
            title={tn(`primary.${nav.id}.full` as 'primary.home.full')}
            className={({ isActive }) => clsx(
              "w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 group relative",
              isActive 
                ? "bg-primary/10 text-primary" 
                : "hover:bg-muted/50 text-muted-foreground/60"
            )}
          >
            {({ isActive }) => (
              <>
                <nav.icon size={20} strokeWidth={isActive ? 2.5 : 2} />
                {isActive && (
                  <div className="absolute -left-2 w-1 h-6 bg-primary rounded-full" aria-hidden />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>
      </div>

      {/* Bottom icons — outside scroll container so popovers are not clipped */}
      <div className="flex shrink-0 flex-col items-center gap-4 px-2 pb-4 pt-2">
        <NotificationBell />
        <NavLink
          to="/settings"
          title={tn('tooltips.settingsProfile')}
          className={({ isActive }) => clsx(
            "w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 group relative",
            isActive
              ? "bg-primary/10 text-primary"
              : "hover:bg-muted/50 text-muted-foreground/60"
          )}
        >
          <Settings size={20} />
        </NavLink>
      </div>
    </aside>
  );
};

/**
 * Pane 2: Middle list bar for specific pages (Unsaved/Projects)
 */
export const SubSidebar: React.FC<SubSidebarProps> = ({
  activeItem,
  onItemSelect,
  onTabDropped,
  mobileOpen = false,
  onMobileOpenChange,
}) => {
  const { t: tc } = useTranslation('common');
  const { t: tToast } = useTranslation('notifications');
  const { t: tn } = useTranslation('nav');

  const logger = React.useMemo(() => new Logger({ tag: 'SubSidebar' }), []);
  // allSessions is the single source of truth — populated by TabsIndexPage on load
  const { sessionUpdate, allSessions, setAllSessions, activeSessionIds, setActiveSessionIds } = useSessionsStore();
  const [isAddModalOpen, setIsAddModalOpen] = React.useState(false);
  const [isCreateTabsGroupModalOpen, setIsCreateTabsGroupModalOpen] = React.useState(false);
  const [isEditTabsGroupModalOpen, setIsEditTabsGroupModalOpen] = React.useState(false);
  const [targetSection, setTargetSection] = React.useState<'PROJECTS' | 'UNSAVED'>('PROJECTS');
  const [createTabsGroupSection, setCreateTabsGroupSection] = React.useState<'PROJECTS' | 'UNSAVED'>('PROJECTS');
  const [editSessionId, setEditSessionId] = React.useState<string>('');
  const [editSessionSection, setEditSessionSection] = React.useState<'PROJECTS' | 'UNSAVED'>('PROJECTS');
  const [editSessionName, setEditSessionName] = React.useState<string>('');
  const [editSessionIcon, setEditSessionIcon] = React.useState<string>('📁');
  const [editSessionContextRules, setEditSessionContextRules] = React.useState<
    import('@/types/session-context').SessionContextRules | undefined
  >(undefined);
  const [editSessionCloseTabsOnSessionChange, setEditSessionCloseTabsOnSessionChange] =
    React.useState(false);
  const [editSessionAutoSuspend, setEditSessionAutoSuspend] = React.useState<
    Partial<AutoSuspendConfig> | undefined
  >(undefined);
  const [learnRulePrompt, setLearnRulePrompt] = React.useState<{
    host: string;
    sid: string;
    sessionName: string;
  } | null>(null);
  const [isMoveModalOpen, setIsMoveModalOpen] = React.useState(false);
  const [sessionToMove, setSessionToMove] = React.useState<{ id: string; name: string; section: string } | null>(null);
  const [isBookmarkModalOpen, setIsBookmarkModalOpen] = React.useState(false);
  const [sessionToBookmark, setSessionToBookmark] = React.useState<{ id: string; name: string; section: string } | null>(null);
  const [shareProjectSession, setShareProjectSession] = React.useState<{ sid: string; name: string } | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [sessionToDelete, setSessionToDelete] = React.useState<{ id: string; name: string; section: string; isWorkSpace: boolean } | null>(null);
  const { sendCBMessage, sendMessage } = useExtensionUtil();
  const detectedState = useHasExtension();
  // null  = still pending → keep UNSAVED visible (no flicker)
  // true  = extension confirmed present
  // false = extension confirmed absent → hide UNSAVED
  const hasExtension = detectedState !== false || window.troveApp?.isExtension?.();
  const [contextMenu, setContextMenu] = React.useState<{
    x: number;
    y: number;
    anchorTop: number;
    itemId: string;
    section: string;
  } | null>(null);
  const contextMenuRef = React.useRef<HTMLDivElement>(null);
  const [dropTargetSessionId, setDropTargetSessionId] = React.useState<string | null>(null);
  const [isSyncingProjects, setIsSyncingProjects] = React.useState(false);
  const [isResolvingSyncConflict, setIsResolvingSyncConflict] = React.useState(false);
  const [syncConflictProjects, setSyncConflictProjects] = React.useState<SyncConflictProject[]>([]);
  const [isSyncConflictModalOpen, setIsSyncConflictModalOpen] = React.useState(false);

  // Parent often passes a new onItemSelect / i18n fns each render; including them in the
  // sessionUpdate effect deps re-runs the effect with the SAME payload and loops (changesession).
  const onItemSelectRef = React.useRef(onItemSelect);
  const tToastRef = React.useRef(tToast);
  const tcRef = React.useRef(tc);
  const activeItemRef = React.useRef(activeItem);
  React.useLayoutEffect(() => {
    onItemSelectRef.current = onItemSelect;
    tToastRef.current = tToast;
    tcRef.current = tc;
    activeItemRef.current = activeItem;
  });

  // Listen for session updates from extension content script
  React.useEffect(() => {
    if (!sessionUpdate?.type) {
      logger.warn('Received session update with no type, ignoring', { sessionUpdate });
      return;
    }

    const update = sessionUpdate as SessionUpdateEvent;
    logger.debug('Session update received', update);
    
    switch(update.type){
      case 'learnRulePrompt':
        if (update.host && update.sid && update.sessionName) {
          setLearnRulePrompt({
            host: String(update.host),
            sid: String(update.sid),
            sessionName: String(update.sessionName),
          });
        }
        break;
      case 'activesessions':
        if (Array.isArray(update.activeSessions)) {
          setActiveSessionIds(
            update.activeSessions.filter(
              (id): id is string => typeof id === 'string' && id !== ''
            )
          );
        }
        break;
      case 'updatesessionname':
        // Update session name for any session type
        if (update.csid && update.name) {
          setAllSessions(prev => 
            prev.map(s => 
              s.sid === update.csid
                ? {
                    ...s,
                    name: update.name as string,
                    icon: update.icon ?? s.icon,
                    ...(update.closeTabsOnSessionChange !== undefined
                      ? {
                          closeTabsOnSessionChange: Boolean(
                            update.closeTabsOnSessionChange
                          ),
                        }
                      : {}),
                    ...(update.autoSuspend !== undefined
                      ? { autoSuspend: update.autoSuspend as AutoSuspendConfig }
                      : {}),
                  }
                : s
            )
          );
        }
        break;
      case 'autosuspendcounts':
        if (update.csid && typeof update.sleepingTabCount === 'number') {
          setAllSessions(prev =>
            prev.map(s =>
              s.sid === update.csid
                ? { ...s, sleepingTabCount: update.sleepingTabCount as number }
                : s
            )
          );
        }
        break;
      case 'replaced':
        /*
        {
            "type": "replaced",
            "tabId": 2000263226,
            "oldTabId": 2000263222,
            "csid": "mm9h5481lv80"
        }
        */
        if (update.csid && update.tabId && update.oldTabId) {
          setAllSessions(prev => 
            prev.map(s => {
              const sTabId = parseInt(String(update.tabId), 10);
              if (s.sid === update.csid) {
                const tabIdIndex = s.tabOrgId?.indexOf(update.oldTabId as string | number);
                if (tabIdIndex !== undefined && tabIdIndex !== -1) {
                  const newTabOrgId = [...(s.tabOrgId || [])];
                  newTabOrgId[tabIdIndex] = sTabId;
                  return { ...s, tabOrgId: newTabOrgId };
                }else{
                  logger.debug('Old tab ID not found in session for replacement', { oldTabId: update.oldTabId, sessionId: update.csid });
                }
              }
              return s;
            })
          );
          // Also update the currentSessionTabOrgIdMap so getTabOrgId() returns the new Chrome tab ID
          const { getTabIdFromOrgId, updateTabOrgId } = useSessionsStore.getState();
          const internalTabId = getTabIdFromOrgId(update.oldTabId as string | number);
          if (internalTabId) {
            updateTabOrgId(internalTabId, parseInt(String(update.tabId), 10));
          }
        }
        break;
      case 'changesession': {
        const csid = update.csid || '';
        if (!csid) break;
        // Avoid re-navigation loops when effect re-ran only because an unstable callback identity changed.
        if (csid === activeItemRef.current) {
          logger.debug('changesession: already active, skip', { csid });
          break;
        }
        onItemSelectRef.current?.(csid);
        break;
      }
      case 'groupbydomain':
        // After grouping by domain, the session structure changes but the name and sid remain the same, so we can trigger a refresh by updating the sessions state with the same sessions (to trigger re-render) - ideally the extension would send the updated session data back to us to avoid this extra fetch
        /*
        {
              type: 'groupbydomain',
              csid: sid,
              session: {},
              tabData: [],
            }
        */
        break;
      case 'removeduplicatetabs': {
        const csid = update.csid || '';
        const sess = update.session as Session | undefined;
        if (csid && sess?.tabId && sess?.tabOrgId) {
          setAllSessions(prev =>
            prev.map(s =>
              s.sid === csid
                ? { ...s, tabId: sess.tabId, tabOrgId: sess.tabOrgId }
                : s
            )
          );
        }
        const removed = update.removed ?? 0;
        if (removed > 0) {
          notifications.success(
            tToastRef.current('sidebar.duplicateTabsRemoved', { count: removed })
          );
        } else {
          notifications.info(tToastRef.current('sidebar.noDuplicateTabs'));
        }
        break;
      }
      case 'deletesession':
        if (update.status === 'error') {
          logger.error('Session deletion failed', {
            sessionId: update.sid,
            reason: update.reason,
            status: update.status,
          });
          notifications.error(
            tToastRef.current('sidebar.deleteSessionFailed', {
              reason: update.reason || tcRef.current('common.unknownError'),
            })
          );
        } else {
          setAllSessions(prev => 
            prev.filter(s => s.sid !== update.sid)
          );
        }
        break;
      case 'newsession':
        if (update.session) {
          const sess = update.session as Session;
          setAllSessions(prev => {
            if (prev.some((s) => s.sid === sess.sid)) return prev;
            return [...prev, { ...sess, isWorkSpace: (sess as Session).isWorkSpace }];
          });
        }
        break;
      case 'sessionrestored':
        if (update.sid && update.session) {
          const sess = update.session as Session;
          setAllSessions((prev) =>
            prev.map((s) =>
              s.sid === update.sid
                ? { ...s, ...sess, isWorkSpace: sess.isWorkSpace === true }
                : s
            )
          );
        }
        break;
      case 'moveworkspace': {
        const { data } = update as SessionUpdateEvent;
        const csession = data?.csession;
        const nsession = data?.nsession;
        if (!csession?.sid && !nsession?.sid) break;

        if (csession?.sid) {
          const moved = csession.tabId ?? [];
          setAllSessions((prev) =>
            prev.map((s) => {
              if (s.sid !== csession.sid) return s;
              if (moved.length === 0) {
                return { ...s, tabId: [], tabOrgId: [] };
              }
              const movedSet = new Set(moved.map((id) => String(id)));
              const tabIdArr = s.tabId ?? [];
              const orgArr = s.tabOrgId ?? [];
              const newTabId: string[] = [];
              const newTabOrgId: (number | string)[] = [];
              for (let i = 0; i < tabIdArr.length; i++) {
                if (!movedSet.has(String(tabIdArr[i]))) {
                  newTabId.push(tabIdArr[i]);
                  if (orgArr[i] !== undefined) newTabOrgId.push(orgArr[i]);
                }
              }
              return { ...s, tabId: newTabId, tabOrgId: newTabOrgId };
            })
          );
        }

        void loadSessionsViaExtensionOrApi(sendCBMessage).then(({ sessions }) => {
          if (sessions.length === 0) return;
          setAllSessions(sessions);
        });
        break;
      }
      case 'deleted':
        if (update.tabId && update.csid) {
          // Find which session has this tabId and tabOrgId and remove it from local state
          setTimeout(() => {
            setAllSessions(prev => prev.map(s => {
              const sTabId = parseInt(String(update.tabId), 10);
              const inx = s.tabOrgId?.indexOf(sTabId);
              if (s.sid === update.csid && inx !== undefined && inx !== -1) {
                const cTabId = s.tabId?.[inx];
                const newTabId = s.tabId?.filter(id => id !== cTabId);
                const newTabOrgId = s.tabOrgId?.filter(id => id !== sTabId);
                return { ...s, tabId: newTabId, tabOrgId: newTabOrgId };
              }
              return s;
            }));
            logger.debug('Tab deleted from session in local state', { tabId: update.tabId, sessionId: update.csid });
          }, 150);
        }
        break;
      case 'moved':
          if (update.tabId && update.csid) {
            setTimeout(() => {
              setAllSessions(prev => prev.map(s => {
                if (s.sid === update.csid) {
                  const currentTabId = s.tabId || [];
                  const currentTabOrgId = s.tabOrgId || [];
                  const cTabId = parseInt(String(update.tabId), 10);
                  const tabOrgInx = currentTabOrgId.findIndex(id => id === cTabId);
                  const targetInx = update.inx;
                  if (targetInx === undefined || targetInx < 0 || targetInx > currentTabId.length) {
                    logger.debug('Invalid target index for move', { 
                      targetIndex: targetInx, 
                      sessionId: update.csid 
                    });
                    return s;
                  }
                  if(tabOrgInx === targetInx) {
                    logger.debug('Tab is already at the target index, no move needed');
                    return s;
                  }
                  if (tabOrgInx === -1) {
                    logger.debug('Tab to move not found in session', { tabOrgId: update.tabId, sessionId: update.csid });
                    return s;
                  }
                  const tabIdToMove = currentTabId[tabOrgInx];
                  const tabOrgIdToMove = currentTabOrgId[tabOrgInx];
                  if (!tabIdToMove || !tabOrgIdToMove) {
                    logger.debug('Tab ID or Org ID to move not found', { tabId: tabIdToMove, tabOrgId: tabOrgIdToMove, sessionId: update.csid });
                    return s;
                  }
                  // Remove the tab from its current position
                  const newTabId = currentTabId.filter(id => id !== tabIdToMove);
                  const newTabOrgId = currentTabOrgId.filter(id => id !== tabOrgIdToMove);
                  // Insert the tab at the new position
                  newTabId.splice(targetInx, 0, tabIdToMove);
                  newTabOrgId.splice(targetInx, 0, tabOrgIdToMove);
                  logger.debug('Tab moved within session in local state', { 
                    tabId: update.tabId, 
                    sessionId: update.csid,
                    newIndex: targetInx
                  });
                  return { ...s, tabId: newTabId, tabOrgId: newTabOrgId };
                }
                return s;
              }));
            }, 150);
          }
        break;
      case 'created':
        if (update.tabId && update.tab && update.csid) {
          const newTabIdFromTab = update.tab.tabId as string;
          const newTabOrgId = update.tabId as string | number;

          // Find which session has this tabId and tabOrgId and add it to local state at specific index
          setAllSessions(prev => prev.map(s => {
            if (s.sid === update.csid) {
              const insertIndex = update.inx ?? s.tabId.length;
              const currentTabId = s.tabId || [];
              const currentTabOrgId = s.tabOrgId || [];
              
              // Check for duplicates before inserting
              if (currentTabId.includes(newTabIdFromTab) || currentTabOrgId.includes(newTabOrgId)) {
                logger.debug('Tab already exists in session, skipping duplicate', { 
                  tabId: newTabIdFromTab,
                  tabOrgId: newTabOrgId,
                  sessionId: s.sid 
                });
                return s;
              }
              
              // Insert at specific index (fixed: tabId gets tab.tabId, tabOrgId gets tabId)
              const newTabId = [
                ...currentTabId.slice(0, insertIndex),
                newTabIdFromTab,
                ...currentTabId.slice(insertIndex)
              ];
              const updatedTabOrgId: (string | number)[] = [
                ...currentTabOrgId.slice(0, insertIndex), 
                newTabOrgId,
                ...currentTabOrgId.slice(insertIndex)
              ];
              
              return { ...s, tabId: newTabId, tabOrgId: updatedTabOrgId };
            }
            return s;
          }));

          // Register the tab→orgId mapping if not already tracked
          const { getTabOrgId, updateTabOrgId } = useSessionsStore.getState();
          if (!getTabOrgId(newTabIdFromTab)) {
            updateTabOrgId(newTabIdFromTab, newTabOrgId);
            logger.debug('Added tab mapping to currentSessionTabOrgIdMap', {
              tabId: newTabIdFromTab,
              tabOrgId: newTabOrgId,
            });
          }

          logger.debug('Tab created event', { 
            tabId: update.tab.tabId, 
            sessionId: update.csid,
            insertIndex: update.inx
          });
        }
        break;
      default:
        logger.debug('Unhandled session update type', { type: update.type });
    }

  }, [sessionUpdate, logger, setAllSessions, sendCBMessage]);

  // Close context menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        setContextMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleContextMenu = (e: React.MouseEvent, itemId: string, section: string) => {
    e.preventDefault();
    e.stopPropagation();
    const anchor = getSessionMenuAnchor(e.currentTarget as HTMLElement);
    const { x, y } = getSessionMenuPosition(anchor, SESSION_MENU_WIDTH, SESSION_MENU_EST_HEIGHT);
    setContextMenu({ x, y, anchorTop: anchor.top, itemId, section });
  };

  const handleMoreClick = (e: React.MouseEvent<HTMLButtonElement>, itemId: string, section: string) => {
    e.preventDefault();
    e.stopPropagation();
    const anchor = e.currentTarget.getBoundingClientRect();
    const { x, y } = getSessionMenuPosition(anchor, SESSION_MENU_WIDTH, SESSION_MENU_EST_HEIGHT);
    setContextMenu({ x, y, anchorTop: anchor.top, itemId, section });
  };

  useLayoutEffect(() => {
    if (!contextMenu || !contextMenuRef.current) return;
    const el = contextMenuRef.current;
    const r = el.getBoundingClientRect();
    let { x, y } = contextMenu;
    const { anchorTop } = contextMenu;
    if (r.right > window.innerWidth - VIEWPORT_PAD) x += window.innerWidth - VIEWPORT_PAD - r.right;
    if (r.left < VIEWPORT_PAD) x += VIEWPORT_PAD - r.left;
    if (r.bottom > window.innerHeight - VIEWPORT_PAD) {
      y += window.innerHeight - VIEWPORT_PAD - r.bottom;
    }
    if (r.top < VIEWPORT_PAD) {
      y += VIEWPORT_PAD - r.top;
    }
    // Re-measure: keep flush with ⋮ when the menu fits without viewport shift.
    const fitted =
      anchorTop >= VIEWPORT_PAD &&
      anchorTop + r.height <= window.innerHeight - VIEWPORT_PAD;
    if (fitted) {
      y = anchorTop;
    }
    if (x !== contextMenu.x || y !== contextMenu.y) {
      setContextMenu((prev) => (prev ? { ...prev, x, y } : null));
    }
  }, [contextMenu]);

  const handleContextAction = async (action: string) => {
    if (!contextMenu) return;
    
    const { itemId, section } = contextMenu;
    
    switch (action) {
      case 'open':
        // Open session in extension
        sendMessage('tabMgm', { action: 'changeSession', sid: itemId }, () => {
        });
        break;
      case 'view':
        // View session details - select it
        onItemSelect?.(itemId);
        break;
      case 'edit': {
        // Edit session
        const sessionData = allSessions.find(s => s.sid === itemId);
        if (sessionData) {
          // Convert hex icon to emoji if needed
          let iconEmoji = '📁';
          if (sessionData.icon && /^[0-9A-Fa-f]+$/.test(sessionData.icon)) {
            try {
              iconEmoji = String.fromCodePoint(parseInt(sessionData.icon, 16));
            } catch {
              iconEmoji = '📁';
            }
          } else if (sessionData.icon) {
            iconEmoji = sessionData.icon;
          }
          
          setEditSessionId(itemId);
          setEditSessionSection(section as 'PROJECTS' | 'UNSAVED');
          setEditSessionName(sessionData.name);
          setEditSessionIcon(iconEmoji);
          setEditSessionContextRules(
            (sessionData as { contextRules?: import('@/types/session-context').SessionContextRules })
              .contextRules
          );
          setEditSessionCloseTabsOnSessionChange(
            Boolean(
              (sessionData as { closeTabsOnSessionChange?: boolean })
                .closeTabsOnSessionChange
            )
          );
          setEditSessionAutoSuspend(
            (sessionData as { autoSuspend?: Partial<AutoSuspendConfig> }).autoSuspend
          );
          setIsEditTabsGroupModalOpen(true);
        }
        break;
      }
      case 'toggleAutoSuspend': {
        const session = allSessions.find(s => s.sid === itemId);
        const existing = session?.autoSuspend;
        if (!existing) {
          handleContextAction('edit');
          break;
        }
        const next: AutoSuspendConfig = {
          ...DEFAULT_AUTO_SUSPEND_CONFIG,
          ...existing,
          excludeDomains: existing.excludeDomains ?? [],
          enabled: !existing.enabled,
        };
        const persistLocal = () => {
          setAllSessions(prev =>
            prev.map(s => (s.sid === itemId ? { ...s, autoSuspend: next } : s))
          );
          sendMessage('tabMgm', {
            action: 'updatename',
            sid: itemId,
            name: session?.name ?? '',
            icon: session?.icon ?? '',
            autoSuspend: next,
          });
          notifications.success(
            tc(
              next.enabled
                ? 'sessions.memory.autoSuspendEnabledToast'
                : 'sessions.memory.autoSuspendDisabledToast'
            )
          );
        };
        if (section === 'PROJECTS') {
          void api
            .put('/sessions', { id: itemId, autoSuspend: next })
            .then(() => persistLocal())
            .catch(() => notifications.error(tc('unknownError')));
        } else {
          persistLocal();
        }
        break;
      }
      case 'move': {
        // Show selection modal for moving between workspaces
        const session = allSessions.find(s => s.sid === itemId);
        const sessionName = session?.name;
        
        setSessionToMove({ id: itemId, name: sessionName || 'Untitled', section });
        setIsMoveModalOpen(true);
        break;
      }
      case 'bookmark': {
        // Show bookmark modal
        const session = allSessions.find(s => s.sid === itemId);
        const sessionName = session?.name;
        
        setSessionToBookmark({ id: itemId, name: sessionName || 'Untitled', section });
        setIsBookmarkModalOpen(true);
        break;
      }
      case 'share': {
        if (section === 'UNSAVED') {
          notifications.info(tToast('sidebar.sharingOnlySavedProjects'));
          break;
        }
        const session = allSessions.find((s) => s.sid === itemId);
        const sessionName = session?.name ?? tc('sessions.unnamed');
        setShareProjectSession({ sid: itemId, name: sessionName });
        break;
      }
      case 'copyLinks':
        void loadSessionTabsViaExtensionOrApi(sendCBMessage, itemId).then((result) => {
          const links: string[] = [];
          if (result.tabData && result.tabId.length) {
            result.tabId.forEach((tabId) => {
              const tab = result.tabData[tabId];
              if (tab?.url) {
                links.push(tab.url);
              }
            });
          }
          if (links.length > 0) {
            void navigator.clipboard.writeText(links.join('\n'));
            notifications.success(
              tToast('sidebar.copiedLinks', { count: links.length })
            );
          }
        });
        break;
      case 'suspend':
        // Suspend all tabs in session
        sendMessage('tabMgm', {action: 'suspendall', sid: itemId}, () => {
          notifications.success(tToast('sidebar.allTabsSuspended'));
        });
        break;
      case 'closeAll':
        // Close all tabs in session
        sendMessage('tabMgm', {action: 'closealltab', sid: itemId}, () => {
          notifications.success(tToast('sidebar.allTabsClosed'));
        });
        break;
      case 'groupByDomain':
        // Group tabs by domain
        sendMessage('tabMgm', {action: 'groupbydomain', sid: itemId}, () => {
          notifications.success(tToast('sidebar.tabsGroupedByDomain'));
        });
        break;
      case 'removeDuplicateTabs':
        sendMessage(
          'tabMgm',
          { action: 'removeduplicatetabs', sid: itemId },
          () => {}
        );
        break;
      case 'delete': {
        // Delete session - show confirmation modal
        const session = allSessions.find(s => s.sid === itemId);
        const name = session?.name || tc('sessions.unnamed');
        const isWorkSpace = section === 'UNSAVED' ? false : true;
        
        setSessionToDelete({ id: itemId, name, section, isWorkSpace });
        setIsDeleteModalOpen(true);
        break;
      }
    }
    setContextMenu(null);
  };

  const triggerAdd = (section: string) => {
    setTargetSection(section as 'PROJECTS' | 'UNSAVED');
    setIsAddModalOpen(true);
  };

  const triggerCreateTabsGroup = (section: 'PROJECTS' | 'UNSAVED') => {
    setCreateTabsGroupSection(section);
    setIsCreateTabsGroupModalOpen(true);
  };

  const deleteSessionById = React.useCallback(
    async (sid: string, isWorkSpace: boolean) => {
      const ext = await detectExtension();
      const hasExt = ext || window.troveApp?.isExtension?.();

      if (hasExt) {
        sendMessage('tabMgm', {
          action: 'deleteSession',
          sid,
          isWorkSpace,
        });
        return;
      }

      const res = await api.put<{ status: string; message?: string }>(
        '/sessions/workspace/update',
        { type: 'deletesession', sid }
      );
      if (res.data?.status !== 'success') {
        throw new Error(res.data?.message || tc('unknownError'));
      }
      setAllSessions((prev) => prev.filter((s) => s.sid !== sid));
    },
    [sendMessage, setAllSessions, tc]
  );

  const handleDragOver = (e: React.DragEvent, sessionId?: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (sessionId) {
      setDropTargetSessionId(sessionId);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if we're actually leaving the element (not entering a child)
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setDropTargetSessionId(null);
    }
  };

  const handleDrop = async (e: React.DragEvent, isTargetWorkSpace: boolean, targetSessionId?: string) => {
    e.preventDefault();
    
    // Try to parse drag data to check if it's a tab
    let dragData: { type?: string; tabIds?: string[]; tabId?: string; sourceSessionId?: string; isWorkSpace?: boolean } = {};
    try {
      const jsonData = e.dataTransfer.getData('application/json');
      if (jsonData) {
        dragData = JSON.parse(jsonData);
      }
    } catch {
      // Not JSON data, might be a session drag
    }

    console.debug('Drop event data:', { dragData, targetSessionId, isTargetWorkSpace });
    // Handle tab drop
    if ((dragData.type === 'tab') && targetSessionId && dragData.sourceSessionId) {
      const tabIds = dragData.tabIds || (dragData.tabId ? [dragData.tabId] : []);
      
      // Prevent dropping on the same session
      if (dragData.sourceSessionId === targetSessionId || tabIds.length === 0) {
        setDropTargetSessionId(null);
        return;
      }

      try {
        // Get the tabs' original IDs from the store
        const { rawAllSessions } = useSessionsStore.getState();
        const sourceSession = rawAllSessions[dragData.sourceSessionId];
        
        if (sourceSession) {
          console.debug('Source session found for tab move', { sourceSession, tabIds });
          // Send session logical tab ids (same as session.tabId); extension resolves to Chrome tabOrgId for moves.
          const logicalTabIds = tabIds.filter((tid) =>
            (sourceSession.tabId ?? []).some((x) => String(x) === String(tid))
          );

          if (logicalTabIds.length > 0) {
            const sobj = {
              csession: {
                isWorkSpace: dragData.isWorkSpace,
                type: dragData.isWorkSpace ? 'workspace' : 'unsaved',
                sid: dragData.sourceSessionId,
                tabId: logicalTabIds,
              },
              nsession: {
                isWorkSpace: isTargetWorkSpace,
                type: isTargetWorkSpace ? 'workspace' : 'unsaved',
                sid: targetSessionId,
              },
            };

            sendCBMessage('tabMgm', {
              action: 'moveworkspace',
              mode: 'workspace',
              data: sobj,
            }, () => {
              // Notify parent to refresh
              onTabDropped?.();
            });
          } else {
            console.error('No valid logical tab ids for dragged tabs', { tabIds, sourceSession });
          }
        }else{
          console.error('Source session not found for tab move', { sourceSessionId: dragData.sourceSessionId });
        }
      } catch (err) {
        console.error('Failed to move tabs:', err);
      } finally {
        setDropTargetSessionId(null);
      }
      return;
    }
    
    // Clear drop target if not a tab drop
    setDropTargetSessionId(null);
  };

  const useMobileDrawer = onMobileOpenChange != null;
  const closeMobile = () => onMobileOpenChange?.(false);

  const parseExtensionCallback = React.useCallback((rawUnknown: unknown) => {
    const raw = rawUnknown as { data?: Record<string, unknown> } & Record<string, unknown>;
    return raw?.data ?? raw;
  }, []);

  const handleSyncProjects = React.useCallback(() => {
    if (!window.troveApp?.isExtension?.() || isSyncingProjects) return;
    setIsSyncingProjects(true);
    sendCBMessage('syncProjectsFromServer', {}, (rawUnknown: unknown) => {
      setIsSyncingProjects(false);
      const result = parseExtensionCallback(rawUnknown) as {
        status?: string;
        added?: number;
        missingOnServer?: SyncConflictProject[];
      };
      if (result.status !== 'success') {
        notifications.error(tToastRef.current('sidebar.projectsSyncFailed'));
        return;
      }
      const added = result.added ?? 0;
      const missing = result.missingOnServer ?? [];
      if (added > 0) {
        notifications.success(
          tToastRef.current('sidebar.projectsSynced', { count: added })
        );
      }
      if (missing.length > 0) {
        setSyncConflictProjects(missing);
        setIsSyncConflictModalOpen(true);
      } else if (added === 0) {
        notifications.info(tToastRef.current('sidebar.projectsUpToDate'));
      }
    });
  }, [isSyncingProjects, parseExtensionCallback, sendCBMessage]);

  const handleMatchServerSync = React.useCallback(
    async (selectedSids: string[]) => {
      setIsResolvingSyncConflict(true);
      await new Promise<void>((resolve, reject) => {
        sendCBMessage(
          'removeLocalProjectsMissingOnServer',
          { sids: selectedSids },
          (rawUnknown: unknown) => {
            const result = parseExtensionCallback(rawUnknown) as {
              status?: string;
              removed?: number;
              succeededSids?: string[];
              failedSids?: string[];
            };
            if (result.status === 'error') {
              reject(new Error(tToastRef.current('sidebar.projectsRemoveFailed')));
              return;
            }
            const removed = result.removed ?? result.succeededSids?.length ?? 0;
            const failed = result.failedSids?.length ?? 0;
            if (removed > 0) {
              notifications.success(
                tToastRef.current('sidebar.projectsRemoved', { count: removed })
              );
            }
            if (failed > 0) {
              notifications.error(
                tToastRef.current('sidebar.projectsRemovePartial', {
                  failed,
                  total: selectedSids.length,
                })
              );
            }
            setSyncConflictProjects((prev) =>
              prev.filter((p) => !(result.succeededSids ?? []).includes(p.sid))
            );
            if ((result.failedSids ?? []).length === 0) {
              setIsSyncConflictModalOpen(false);
            }
            resolve();
          }
        );
      }).finally(() => setIsResolvingSyncConflict(false));
    },
    [parseExtensionCallback, sendCBMessage]
  );

  const handleRestoreToCloud = React.useCallback(
    async (selectedSids: string[]) => {
      setIsResolvingSyncConflict(true);
      await new Promise<void>((resolve, reject) => {
        sendCBMessage(
          'recoverProjectsToServer',
          { sids: selectedSids },
          (rawUnknown: unknown) => {
            const result = parseExtensionCallback(rawUnknown) as {
              status?: string;
              recovered?: number;
              succeededSids?: string[];
              failedSids?: string[];
            };
            if (result.status === 'error') {
              reject(new Error(tToastRef.current('sidebar.projectsRestoreFailed')));
              return;
            }
            const recovered = result.recovered ?? result.succeededSids?.length ?? 0;
            const failed = result.failedSids?.length ?? 0;
            if (recovered > 0) {
              notifications.success(
                tToastRef.current('sidebar.projectsRestored', { count: recovered })
              );
            }
            if (failed > 0) {
              notifications.error(
                tToastRef.current('sidebar.projectsRestorePartial', {
                  failed,
                  total: selectedSids.length,
                })
              );
            }
            setSyncConflictProjects((prev) =>
              prev.filter((p) => !(result.succeededSids ?? []).includes(p.sid))
            );
            if ((result.failedSids ?? []).length === 0) {
              setIsSyncConflictModalOpen(false);
            }
            resolve();
          }
        );
      }).finally(() => setIsResolvingSyncConflict(false));
    },
    [parseExtensionCallback, sendCBMessage]
  );

  return (
    <>
      {useMobileDrawer && (
        <button
          type="button"
          aria-label={tn('aria.closeSessionList')}
          className={clsx(
            'fixed inset-0 z-30 bg-black/40 transition-opacity md:hidden',
            mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
          )}
          onClick={closeMobile}
        />
      )}
      <aside
        className={clsx(
          'flex flex-col bg-sidebar-2 border-r border-border z-40',
          'w-64 min-w-[240px]',
          useMobileDrawer &&
            'fixed inset-y-0 left-0 z-50 w-[min(280px,88vw)] max-w-[280px] transition-transform duration-200 ease-out md:static md:z-40 md:w-64 md:min-w-[240px] md:max-w-none',
          useMobileDrawer && (mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0')
        )}
      >
      <div className="flex-1 overflow-y-auto custom-scrollbar pt-6 pb-4">


        {MENU_CONFIG.filter(
          (section) =>
            (hasExtension && !isElectron()) || section.title !== 'UNSAVED'
        ).map((section) => {
          const sessionsByType = allSessions.filter(s => 
            section.title === 'UNSAVED' ? s.isWorkSpace === false : s.isWorkSpace === true
          );
          const items = sessionsByType.length > 0
            ? sessionsByType.map(sessionToMenuItem)
            : section.items;
          const addSectionLabel = tn(`sections.add.${section.title.toLowerCase()}`, {
            defaultValue:
              section.type === 'tabs'
                ? section.title === 'PROJECTS'
                  ? 'Create new project'
                  : 'Create new session'
                : `Add to ${tn(section.titleKey)}`,
          });
          const sectionHeaderIconBtnClass =
            'inline-flex items-center justify-center w-6 h-6 shrink-0 rounded text-muted-foreground/60 hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50 disabled:pointer-events-none';
          return (
            <div 
              key={section.title} 
              className="mb-5"
            >
              <div className="mb-2 flex items-center justify-between px-4 group">
                <h3 className="sidebar-section-title">
                  {tn(section.titleKey)}
                </h3>
                {section.showAdd && (
                  <div className="flex items-center gap-0.5">
                    {section.title === 'PROJECTS' && hasExtension === true ? (
                      <button
                        type="button"
                        onClick={handleSyncProjects}
                        disabled={isSyncingProjects}
                        aria-label={tn('sections.sync.projects')}
                        title={tn('sections.sync.projects')}
                        className={sectionHeaderIconBtnClass}
                      >
                        <RefreshCw
                          size={14}
                          strokeWidth={2}
                          className={isSyncingProjects ? 'animate-spin' : undefined}
                        />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => {
                        if (section.type === 'tabs') {
                          triggerCreateTabsGroup(section.title as 'PROJECTS' | 'UNSAVED');
                        } else {
                          triggerAdd(section.title);
                        }
                      }}
                      aria-label={addSectionLabel}
                      title={addSectionLabel}
                      className={sectionHeaderIconBtnClass}
                    >
                      <Plus size={14} strokeWidth={2} />
                    </button>
                  </div>
                )}
              </div>

              <div className="px-3 space-y-0.5 transition-all duration-200 relative">
                {items.map((item) => (
                  <div
                    key={item.id}
                    onContextMenu={(e) => handleContextMenu(e, item.id, section.title)}
                    onDragOver={(e) => handleDragOver(e, item.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, section.title === 'PROJECTS', item.id)}
                    onClick={() => {
                      sendMessage('tabMgm', { action: 'changeSession', sid: item.id }, () => {});
                      onItemSelect?.(item.id);
                      closeMobile();
                    }}
                    className={clsx(
                      "sidebar-nav-item group cursor-pointer",
                      activeItem === item.id
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
                      dropTargetSessionId === item.id ? "ring-2 ring-primary ring-offset-2 scale-105" : ""
                    )}
                  >
                    <div className="flex-1 flex items-center">
                      {item.iconEmoji ? (
                        <span className="mr-3 w-4 h-4 flex items-center justify-center text-base leading-none" aria-hidden>
                          {item.iconEmoji}
                        </span>
                      ) : (
                        <item.icon
                          size={16}
                          className={clsx(
                            "mr-3 transition-colors",
                            activeItem === item.id ? item.color : "text-muted-foreground/60 group-hover:text-muted-foreground/80"
                          )}
                        />
                      )}
                      <span
                        className={clsx(
                          'flex-1 text-left truncate',
                          (activeItem === item.id || activeSessionIds.includes(item.id)) &&
                            'font-semibold'
                        )}
                      >
                        {item.label}
                      </span>
                      {item.sleepingCount !== undefined && item.sleepingCount > 0 ? (
                        <span
                          className="ml-1 shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                          title={tc('sessions.memory.sleepingBadge', {
                            count: item.sleepingCount,
                          })}
                        >
                          <Moon size={10} className="mr-0.5 inline" aria-hidden />
                          {item.sleepingCount}
                        </span>
                      ) : null}
                      {item.count !== undefined && (
                        <span className="sidebar-count bg-muted text-muted-foreground/60">
                          {item.count}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      data-session-more-trigger
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoreClick(e, item.id, section.title);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      aria-label={tc('common.moreActions')}
                      className="ml-2 shrink-0 p-1 rounded text-muted-foreground/60 hover:text-foreground hover:bg-muted/50 opacity-0 group-hover:opacity-100 focus:opacity-100 [@media(hover:none)]:opacity-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                    >
                      <MoreHorizontal size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Item */}
      <div className="p-3 border-t border-border-muted">
        <button
          type="button"
          onClick={() => {
            onItemSelect?.('shared');
            closeMobile();
          }}
          className={clsx(
            "w-full flex items-center px-4 py-3 rounded-lg transition-all duration-200 text-sm font-medium",
            activeItem === 'shared'
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
          )}
        >
          <Share2 size={16} className="mr-3 text-muted-foreground/60" />
          <span>{tn('primary.shared-with-me.short')}</span>
        </button>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: hsl(var(--muted-foreground) / 0.2);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: hsl(var(--muted-foreground) / 0.4);
        }
      `}} />

    </aside>

      {/* Session context menu: portal + viewport coords — aside uses transform (mobile drawer),
          which breaks position:fixed when the menu is a child of aside. */}
      {contextMenu &&
        createPortal(
          <div
            ref={contextMenuRef}
            className="dropdown-menu-panel fixed z-[200] max-h-[calc(100vh-16px)] w-max min-w-[220px] overflow-y-auto border border-border bg-card py-1 shadow-popover custom-scrollbar"
            style={{
              left: `${contextMenu.x}px`,
              top: `${contextMenu.y}px`,
            }}
          >
            <button
              onClick={() => handleContextAction('open')}
              className="w-full flex items-center gap-3 whitespace-nowrap px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
            >
              <ExternalLink size={14} className="shrink-0" />
              Open
            </button>
            <button
              onClick={() => handleContextAction('view')}
              className="w-full flex items-center gap-3 whitespace-nowrap px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
            >
              <Eye size={14} className="shrink-0" />
              View
            </button>
            <button
              onClick={() => handleContextAction('edit')}
              className="w-full flex items-center gap-3 whitespace-nowrap px-4 py-2 text-sm font-medium text-blue-500 hover:bg-blue-500/10 transition-colors"
            >
              <Edit3 size={14} className="shrink-0" />
              Edit
            </button>
            <div className="h-px bg-border my-1" />
            <button
              onClick={() => handleContextAction('move')}
              className="w-full flex items-center gap-3 whitespace-nowrap px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
            >
              <ArrowRight size={14} className="shrink-0" />
              Move to...
            </button>
            <button
              onClick={() => handleContextAction('bookmark')}
              className="w-full flex items-center gap-3 whitespace-nowrap px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
            >
              <Bookmark size={14} className="shrink-0" />
              Add to Bookmarks
            </button>
            <div className="h-px bg-border my-1" />
            <button
              onClick={() => handleContextAction('share')}
              className="w-full flex items-center gap-3 whitespace-nowrap px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
            >
              <Share2 size={14} className="shrink-0" />
              Share
            </button>
            <button
              onClick={() => handleContextAction('copyLinks')}
              className="w-full flex items-center gap-3 whitespace-nowrap px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
            >
              <LinkIcon size={14} className="shrink-0" />
              Copy all links
            </button>
            <div className="h-px bg-border my-1" />
            <button
              onClick={() => handleContextAction('suspend')}
              className="w-full flex items-center gap-3 whitespace-nowrap px-4 py-2 text-sm font-medium text-blue-500 hover:bg-blue-500/10 transition-colors"
            >
              <Pause size={14} className="shrink-0" />
              Suspend all tabs
            </button>
            {(() => {
              const sess = allSessions.find((s) => s.sid === contextMenu?.itemId);
              const autoCfg = sess?.autoSuspend;
              const label = !autoCfg
                ? tc('sessions.memory.autoSuspendConfigure')
                : autoCfg.enabled
                  ? tc('sessions.memory.autoSuspendDisable')
                  : tc('sessions.memory.autoSuspendEnable');
              return (
                <button
                  onClick={() =>
                    handleContextAction(autoCfg ? 'toggleAutoSuspend' : 'edit')
                  }
                  className="w-full flex items-center gap-3 whitespace-nowrap px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
                >
                  <Moon size={14} className="shrink-0" />
                  {label}
                </button>
              );
            })()}
            <button
              onClick={() => handleContextAction('closeAll')}
              className="w-full flex items-center gap-3 whitespace-nowrap px-4 py-2 text-sm font-medium text-orange-500 hover:bg-orange-500/10 transition-colors"
            >
              <XIcon size={14} className="shrink-0" />
              Close all tabs
            </button>
            <button
              onClick={() => handleContextAction('groupByDomain')}
              className="w-full flex items-center gap-3 whitespace-nowrap px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
            >
              <Grid3x3 size={14} className="shrink-0" />
              Group by domain
            </button>
            <button
              onClick={() => handleContextAction('removeDuplicateTabs')}
              className="w-full flex items-center gap-3 whitespace-nowrap px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
            >
              <SquareStack size={14} className="shrink-0" />
              Remove duplicate tabs
            </button>
            <div className="h-px bg-border my-1" />
            <button
              onClick={() => handleContextAction('delete')}
              className="w-full flex items-center gap-3 whitespace-nowrap px-4 py-2 text-sm font-medium text-rose-500 hover:bg-rose-500/10 transition-colors"
            >
              <Trash2 size={14} className="shrink-0" />
              Delete
            </button>
          </div>,
          document.body
        )}

      {/* Modals are intentionally rendered outside <aside> — the aside has transition-transform
          which creates a new CSS containing block, confining position:fixed children to its
          narrow width instead of the full viewport. */}
      <AddGroupModal 
        isOpen={isAddModalOpen} 
        onClose={() => setIsAddModalOpen(false)} 
        onAdd={(g) => {
          console.log(`Adding to ${targetSection}:`, g);
          setIsAddModalOpen(false);
        }}
        title={`Add to ${targetSection.toLowerCase()}`}
      />

      <CreateTabsGroupModal 
        isOpen={isCreateTabsGroupModalOpen}
        section={createTabsGroupSection}
        onClose={() => setIsCreateTabsGroupModalOpen(false)}
        onSuccess={(created?: CreateTabsGroupResult) => {
          setIsCreateTabsGroupModalOpen(false);
          // Optimistic merge so the sidebar + Sessions index reflect the new
          // workspace immediately without requiring a page reload (T-09).
          // Extension-driven `newsession` events still arrive and reconcile
          // tab data, but the sidebar entry is visible from the next paint.
          if (created?.sid) {
            setAllSessions((prev) => {
              if (prev.some((s) => s.sid === created.sid)) return prev;
              return [
                ...prev,
                {
                  sid: created.sid,
                  name: created.name,
                  icon: created.icon,
                  isWorkSpace: created.isWorkSpace,
                  tabId: [],
                  tabOrgId: [],
                },
              ];
            });
          }
        }}
      />

      <EditTabsGroupModal 
        isOpen={isEditTabsGroupModalOpen}
        sessionId={editSessionId}
        section={editSessionSection}
        initialName={editSessionName}
        initialIcon={editSessionIcon}
        initialContextRules={editSessionContextRules}
        initialCloseTabsOnSessionChange={editSessionCloseTabsOnSessionChange}
        initialAutoSuspend={editSessionAutoSuspend}
        onClose={() => setIsEditTabsGroupModalOpen(false)}
        onSuccess={(result) => {
          setIsEditTabsGroupModalOpen(false);
          if (editSessionId && result?.name) {
            setAllSessions((prev) =>
              prev.map((s) =>
                s.sid === editSessionId
                  ? {
                      ...s,
                      name: result.name,
                      icon: result.icon,
                      closeTabsOnSessionChange: result.closeTabsOnSessionChange,
                      autoSuspend: result.autoSuspend,
                    }
                  : s
              )
            );
          }
        }}
      />

      <MoveSessionModal 
        isOpen={isMoveModalOpen}
        sessionName={sessionToMove?.name || ''}
        currentSessionId={sessionToMove?.id || ''}
        onClose={() => {
          setIsMoveModalOpen(false);
          setSessionToMove(null);
        }}
        onSelect={(targetSessionId, _targetSessionName, targetType) => {
          if (sessionToMove) {
            const sourceType = sessionToMove.section === 'UNSAVED' ? false : true;
            
            sendCBMessage('tabMgm', {
              action:'moveworkspace',
              mode: 'workspace',
              data: {
                csession: {
                  isWorkSpace: sourceType,
                  tabId: [],
                  sid: sessionToMove.id
                },
                nsession: {
                  isWorkSpace: targetType,
                  sid: targetSessionId
                }
              }
            }, () => {
              setAllSessions(prev => prev.filter(s => s.sid !== sessionToMove.id));
            });
          }
          setIsMoveModalOpen(false);
          setSessionToMove(null);
        }}
      />

      <BookmarkModal 
        isOpen={isBookmarkModalOpen}
        sessionName={sessionToBookmark?.name || ''}
        sessionId={sessionToBookmark?.id || ''}
        isWorkSpace={sessionToBookmark?.section === 'UNSAVED' ? false : true}
        onClose={() => {
          setIsBookmarkModalOpen(false);
          setSessionToBookmark(null);
        }}
        onSuccess={() => {
          console.log('Bookmarked successfully');
        }}
      />

      <ShareProjectModal
        isOpen={Boolean(shareProjectSession)}
        onClose={() => setShareProjectSession(null)}
        projectSid={shareProjectSession?.sid ?? ''}
        projectName={shareProjectSession?.name}
      />

      <DeleteSessionModal 
        isOpen={isDeleteModalOpen}
        sessionId={sessionToDelete?.id || ''}
        sessionName={sessionToDelete?.name || ''}
        isWorkSpace={sessionToDelete?.isWorkSpace || false}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSessionToDelete(null);
        }}
        onConfirm={async () => {
          if (!sessionToDelete) return;
          await deleteSessionById(sessionToDelete.id, sessionToDelete.isWorkSpace);
        }}
      />

      <SyncProjectsConflictModal
        isOpen={isSyncConflictModalOpen}
        projects={syncConflictProjects}
        isResolving={isResolvingSyncConflict}
        onClose={() => {
          if (!isResolvingSyncConflict) setIsSyncConflictModalOpen(false);
        }}
        onMatchServer={handleMatchServerSync}
        onRestoreToCloud={handleRestoreToCloud}
      />

      {learnRulePrompt && (
        <LearnRulePrompt
          host={learnRulePrompt.host}
          sid={learnRulePrompt.sid}
          sessionName={learnRulePrompt.sessionName}
          onDismiss={() => setLearnRulePrompt(null)}
        />
      )}
    </>
  );
};
